import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownIcon, ArrowUpIcon, SearchIcon } from 'lucide-react';
import { CountryRow } from '../../types/census';
import { formatCompact, formatInt, formatPct } from '../../utils/format';
import { agentsPerCapitaFor } from '../../utils/estimates';
import { useScenario } from '../../contexts/ScenarioContext';
import { SourceRef } from '../SourceRef';
import { DownloadButtons } from '../DownloadButtons';
type SortKey = 'rank' | 'name' | 'population' | 'internetPct' | 'internetUsers' | 'medianAge' | 'mobile' | 'endpoints' | 'agentsPerCapita';
type RankedRow = CountryRow & {
  rank: number;
  scenarioAgents: number | null;
};
interface Column {
  key: SortKey;
  label: string;
  numeric: boolean;
  sourceId?: string;
  estimate?: boolean;
}
const COLUMNS: Column[] = [{
  key: 'rank',
  label: 'Rank',
  numeric: true
}, {
  key: 'name',
  label: 'Country',
  numeric: false
}, {
  key: 'population',
  label: 'Population',
  numeric: true,
  sourceId: 'restcountries'
}, {
  key: 'internetPct',
  label: 'Internet %',
  numeric: true,
  sourceId: 'wbInternet'
}, {
  key: 'internetUsers',
  label: 'Internet users',
  numeric: true,
  sourceId: 'wbInternet'
}, {
  key: 'medianAge',
  label: 'Median age',
  numeric: true,
  sourceId: 'unwpp'
}, {
  key: 'mobile',
  label: 'Mobile / 100',
  numeric: true,
  sourceId: 'wbMobile'
}, {
  key: 'endpoints',
  label: 'Assistant endpoints',
  numeric: true,
  sourceId: 'censusModel',
  estimate: true
}, {
  key: 'agentsPerCapita',
  label: 'Agents / capita',
  numeric: true,
  sourceId: 'censusModel',
  estimate: true
}];
const getters: Record<SortKey, (r: RankedRow) => number | string | null> = {
  rank: r => r.rank,
  name: r => r.name,
  population: r => r.population,
  internetPct: r => r.internetPct?.value ?? null,
  internetUsers: r => r.internetUsers,
  medianAge: r => r.medianAge,
  mobile: r => r.mobilePer100?.value ?? null,
  endpoints: r => r.endpoints,
  agentsPerCapita: r => r.scenarioAgents
};
const PAGE = 50;
export function CountryTable({
  rows
}: {
  rows: CountryRow[];
}) {
  const [sortKey, setSortKey] = useState<SortKey>('rank');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const {
    scenarioId,
    scenario
  } = useScenario();
  const ranked: RankedRow[] = useMemo(() => [...rows].sort((a, b) => b.population - a.population).map((r, i) => ({
    ...r,
    rank: i + 1,
    scenarioAgents: agentsPerCapitaFor(r, scenarioId)
  })), [rows, scenarioId]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? ranked.filter(r => r.name.toLowerCase().includes(q) || r.cca3.toLowerCase().includes(q) || r.region.toLowerCase().includes(q)) : ranked;
    const get = getters[sortKey];
    return [...list].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'string' ? va.localeCompare(String(vb)) : (va as number) - (vb as number);
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [ranked, query, sortKey, dir]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const current = Math.min(page, pages - 1);
  const visible = showAll ? filtered : filtered.slice(current * PAGE, current * PAGE + PAGE);
  const sortBy = (key: SortKey) => {
    if (key === sortKey) setDir(d => d === 'asc' ? 'desc' : 'asc');else {
      setSortKey(key);
      setDir(key === 'name' || key === 'rank' ? 'asc' : 'desc');
    }
    setPage(0);
  };
  return <div data-id="element-267" data-mp="39r2wg2savw6i">
      <div className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-268" data-mp="39rclno5itzb5">
        <label className="relative block w-full sm:max-w-xs" data-id="element-269" data-mp="t276omgrzv8a">
          <span className="sr-only" data-id="element-270" data-mp="oxzgslyl098h">Search countries</span>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-271" data-mp="1k97dh5giz9xd" />
          <input type="search" value={query} onChange={e => {
          setQuery(e.target.value);
          setPage(0);
        }} placeholder="Search country, code or region" className="w-full rounded-md border border-line bg-surface py-2 pl-9 pr-3 text-sm text-ink placeholder:text-faint focus:border-machine focus:outline-none" data-id="element-272" data-mp="1obapbv73wgj6" />
        </label>
        <div className="flex items-center gap-4" data-id="element-273" data-mp="277ahp73zxm3a">
          <p className="font-mono text-[12px] text-muted" aria-live="polite" data-id="element-274" data-mp="2bztyse1w6zcu">
            {filtered.length} of {ranked.length} countries
          </p>
          <DownloadButtons name="countries" sourceNote={`Agents per capita follow the ${scenario.label} scenario. Estimates are our model; see /methodology.`} getRows={() => filtered.map(r => ({
          rank: r.rank,
          cca3: r.cca3,
          country: r.name,
          population: r.population,
          internet_pct: r.internetPct?.value ?? null,
          internet_year: r.internetPct?.year ?? null,
          internet_users: r.internetUsers != null ? Math.round(r.internetUsers) : null,
          median_age: r.medianAge,
          mobile_per_100: r.mobilePer100?.value ?? null,
          assistant_endpoints_est: r.endpoints != null ? Math.round(r.endpoints) : null,
          agents_per_capita_est: r.scenarioAgents,
          scenario: scenario.label
        }))} data-id="element-275" data-mp="2h114m7kh5ogv" />
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-line" data-id="element-276" data-mp="jaunps9o5vkq">
        <table className="w-full min-w-[1080px] border-collapse text-[13.5px]" data-id="element-277" data-mp="1grjn7fb5clqy">
          <thead className="bg-surface" data-id="element-278" data-mp="1c09uu6y7r4bd">
            <tr data-id="element-279" data-mp="1bzi1l2s39io9">
              {COLUMNS.map(c => {
              const active = c.key === sortKey;
              return <th key={c.key} scope="col" aria-sort={active ? dir === 'asc' ? 'ascending' : 'descending' : 'none'} className={`whitespace-nowrap border-b border-line px-3 py-2.5 font-normal ${c.numeric ? 'text-right' : 'text-left'}`} data-id="element-280" data-mp="36a8bah2y55mg">
                    <button type="button" onClick={() => sortBy(c.key)} className={`inline-flex items-center gap-1 text-[12px] transition-colors duration-150 ${active ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-281" data-mp="2nh4dqkmly8mk">
                      {c.label}
                      {c.estimate && <span className="text-machine" data-id="element-282" data-mp="2iit3w9wbby4c">*</span>}
                      {active ? dir === 'asc' ? <ArrowUpIcon className="h-3 w-3" data-id="element-283" data-mp="bsmo806g0dbt" /> : <ArrowDownIcon className="h-3 w-3" data-id="element-284" data-mp="gr6a60l1dp46" /> : <span className="w-3" data-id="element-285" data-mp="gq51g2rhtepm" />}
                    </button>
                    {c.sourceId && <SourceRef id={c.sourceId} estimate={c.estimate ? {
                  low: '−15%',
                  high: '+15%'
                } : undefined} data-id="element-286" data-mp="218fkzwgwh6ru" />}
                  </th>;
            })}
            </tr>
          </thead>
          <tbody className="font-mono tabular" data-id="element-287" data-mp="2b03fy7co15y2">
            {visible.map(r => <tr key={r.cca3} className="border-b border-line/60 last:border-b-0 hover:bg-surface" data-id="element-288" data-mp="267s3y64nhtp5">
                <td className="px-3 py-2 text-right text-muted" data-id="element-289" data-mp="2jyx8nmmvlc6c">{r.rank}</td>
                <td className="px-3 py-2 font-sans" data-id="element-290" data-mp="2f0n9l0y15rfg">
                  <Link to={`/countries/${r.cca3}`} className="flex items-center gap-2.5 text-ink hover:text-machine" data-id="element-291" data-mp="2f03t7oo13b3j">
                    <img src={r.flag} alt="" loading="lazy" className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover ring-1 ring-line" data-id="element-292" data-mp="2tvtibvoig4ch" />
                    <span className="truncate" data-id="element-293" data-mp="zjd3w7lk9jwj">{r.name}</span>
                  </Link>
                </td>
                <td className="px-3 py-2 text-right text-ink" data-id="element-294" data-mp="ulbpw12zsarx">{formatInt(r.population)}</td>
                <td className="px-3 py-2 text-right" data-id="element-295" data-mp="8s4k77ldaamw">{r.internetPct ? formatPct(r.internetPct.value) : <Dash data-id="element-296" data-mp="dqpiv2kz0m98" />}</td>
                <td className="px-3 py-2 text-right" data-id="element-297" data-mp="dpny26e5kleb">{r.internetUsers != null ? formatCompact(r.internetUsers) : <Dash data-id="element-298" data-mp="ih5zswo02q0c" />}</td>
                <td className="px-3 py-2 text-right" data-id="element-299" data-mp="ihxt20u4kbng">{r.medianAge != null ? r.medianAge.toFixed(1) : <Dash data-id="element-300" data-mp="nf7ivhlfepbt" />}</td>
                <td className="px-3 py-2 text-right" data-id="element-301" data-mp="niljolefyqh5">{r.mobilePer100 ? r.mobilePer100.value.toFixed(1) : <Dash data-id="element-302" data-mp="3lzza90lheyqa" />}</td>
                <td className="px-3 py-2 text-right text-machine" data-id="element-303" data-mp="3qx7vja1xfivr">{r.endpoints != null ? formatCompact(r.endpoints) : <Dash data-id="element-304" data-mp="3vwc2rv5aj6jg" />}</td>
                <td className="px-3 py-2 text-right text-machine" data-id="element-305" data-mp="3vw2k6smks1ns">
                  {r.scenarioAgents != null ? r.scenarioAgents < 0.1 ? r.scenarioAgents.toFixed(4) : r.scenarioAgents.toFixed(2) : <Dash data-id="element-306" data-mp="1b6mopkcevort" />}
                </td>
              </tr>)}
            {visible.length === 0 && <tr data-id="element-307" data-mp="1f6neu28vhoep">
                <td colSpan={COLUMNS.length} className="px-3 py-10 text-center font-sans text-muted" data-id="element-308" data-mp="1f63tscnxejwg">
                  No countries match “{query}”.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-id="element-309" data-mp="2qx5q9rwqajfi">
        <p className="text-[12px] text-muted" data-id="element-310" data-mp="2qv2qig5rxbxl">
          <span className="text-machine" data-id="element-311" data-mp="2lx9lhqg7inyw">*</span> Our estimate — agents per capita follow the active scenario (<span className="text-ink" data-id="element-312" data-mp="2h0asjb8a4n2a">{scenario.label}</span>). Formula on the methodology page.
        </p>
        <div className="flex items-center gap-2" data-id="element-313" data-mp="2bz3nvl1fo3zu">
          {!showAll && <>
              <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)} className="rounded-md border border-line px-3 py-1.5 text-sm text-ink disabled:opacity-40" data-id="element-314" data-mp="27zkyod2kx6r7">
                Previous
              </button>
              <span className="font-mono text-[12px] text-muted" data-id="element-315" data-mp="653w79hnj1dg">
                {current + 1} / {pages}
              </span>
              <button type="button" disabled={current >= pages - 1} onClick={() => setPage(current + 1)} className="rounded-md border border-line px-3 py-1.5 text-sm text-ink disabled:opacity-40" data-id="element-316" data-mp="25z1ac9toa1ih">
                Next
              </button>
            </>}
          <button type="button" onClick={() => setShowAll(s => !s)} className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:border-machine" data-id="element-317" data-mp="2ez6f0c7helsy">
            {showAll ? 'Paginate (50 / page)' : 'Load all'}
          </button>
        </div>
      </div>
    </div>;
}
function Dash() {
  return <span className="text-faint" data-id="element-318" data-mp="1i7m5a9aih63j">—</span>;
}